/**
 * @fileoverview Local-first service for migration dataset templates.
 * @module system-web/dataset-template-service
 *
 * Responsibilities:
 * - Read the persisted dataset template list from Dexie (liveQuery-safe).
 * - POST create / update / delete / activate / deactivate calls through system-api.
 * - Expose a query helper for a single template by ID.
 *
 * Dependencies:
 * - `@firesquirrel/local-db` for system Dexie access.
 * - `../apiClient` `apiFetch` for API calls.
 *
 * Architecture:
 * - Server-authoritative: all writes go through system-api routes.
 * - Dexie `legacy_migration_dataset_templates` mirrors server state via system sync pull.
 * - `listDatasetTemplates` and `getDatasetTemplate` are liveQuery-safe pure reads.
 * - Mutation functions (create, update, activate, deactivate, softDelete, sample) POST to server;
 *   Dexie receives the authoritative result via the next system sync pull.
 *
 * Notes:
 * - `listDatasetTemplates` is liveQuery-safe (pure Dexie read — never writes).
 * - Mutation functions must NEVER be called inside `liveQuery`.
 * - The sync-config entry for this table is: `legacy_migration_dataset_templates → legacyMigrationDatasetTemplates`.
 *
 * @localFirst
 * Source of Truth: system Mongo `legacyMigrationDatasetTemplates` (server-authoritative).
 * Write Flow: POST/PUT/DELETE to /api/system/legacy-migration/dataset-templates/*; sync pulls result.
 * Sync Strategy: Pull-based after server write.
 * Conflict Resolution: Server wins. optimistic concurrency enforced via version field.
 */

import { getSystemDb } from '@firesquirrel/local-db';
import { apiFetch } from '../apiClient';
import type { MigrationDatasetTemplate } from '../types/legacyMigrationTypes';

// ---------------------------------------------------------------------------
// Local Dexie doc shape (may include extra Dexie metadata fields)
// ---------------------------------------------------------------------------

/**
 * @contract DatasetTemplateDexieDoc
 *
 * Purpose:
 * - Shape of a row in the `legacy_migration_dataset_templates` Dexie table.
 * - Identical to `MigrationDatasetTemplate` plus optional Dexie metadata.
 *
 * Fields: All fields from MigrationDatasetTemplate plus _deleted tombstone.
 *
 * Rules:
 * - _deleted === true rows must be filtered out before returning to callers.
 *
 * Sync:
 * - Server-authoritative; written only via server sync pull.
 */
export type DatasetTemplateDexieDoc = MigrationDatasetTemplate & { _deleted?: boolean };

// ---------------------------------------------------------------------------
// Request/response shapes for service mutations
// ---------------------------------------------------------------------------

/**
 * @contract CreateDatasetTemplateInput
 *
 * Purpose: Request body for creating a new dataset template (POST /dataset-templates).
 * Fields: All required fields for a new draft template (no _id, status, version, timestamps).
 * Rules: key must be URL-safe; displayName must be non-empty.
 * Sync: Server generates _id, status='draft', version=1, timestamps.
 */
export interface CreateDatasetTemplateInput {
  key: string;
  displayName: string;
  description: string;
  kind: 'system-known' | 'user-defined';
  source: MigrationDatasetTemplate['source'];
  target: MigrationDatasetTemplate['target'];
  fieldMappings: MigrationDatasetTemplate['fieldMappings'];
  composite?: MigrationDatasetTemplate['composite'];
}

/**
 * @contract UpdateDatasetTemplateInput
 *
 * Purpose: Request body for updating an existing draft template (PUT /dataset-templates/:id).
 * Fields: Subset of MigrationDatasetTemplate fields that may be changed in a draft.
 * Rules: Can only update draft templates (server returns 409 if active).
 * Sync: Server increments version; result available via next sync pull.
 */
export type UpdateDatasetTemplateInput = Partial<
  Pick<MigrationDatasetTemplate, 'displayName' | 'description' | 'source' | 'target' | 'fieldMappings' | 'composite'>
>;

/**
 * @contract SampleDatasetTemplateInput
 *
 * Purpose: Request body for triggering AI data sampling.
 * Fields: sampleSize, sampleScopeType, optional sampleDeptId.
 * Rules: sampleSize must be between 1 and 500.
 * Sync: Server writes result to aiMetadata; available via next sync pull.
 */
export interface SampleDatasetTemplateInput {
  sampleSize: number;
  sampleScopeType: 'random' | 'department';
  sampleDeptId?: number;
}

// ---------------------------------------------------------------------------
// Dexie reads (liveQuery-safe)
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: system Dexie `legacy_migration_dataset_templates` (mirrored from Mongo).
 * Write Flow: Mutations POST to server; sync pulls result.
 * Sync Strategy: Pull-based after server write.
 * Conflict Resolution: Server wins.
 *
 * Return value is suitable for use inside `liveQuery(() => listDatasetTemplates(...))`.
 * NEVER write to Dexie inside liveQuery.
 *
 * @errorHandling
 * Strategy:   Returns empty array when table is empty. Throws on Dexie open failure.
 * User Impact: Grid shows empty state when no templates exist.
 * Logging:     Not required for a pure read.
 */
export async function listDatasetTemplates(
  statusFilter: 'all' | 'draft' | 'active' = 'all',
): Promise<MigrationDatasetTemplate[]> {
  const db = await getSystemDb();
  const rows = await db.legacy_migration_dataset_templates.toArray() as DatasetTemplateDexieDoc[];
  const live = rows.filter((row) => !row._deleted);
  if (statusFilter === 'all') return live;
  return live.filter((row) => row.status === statusFilter);
}

/**
 * @localFirst
 * Source of Truth: system Dexie `legacy_migration_dataset_templates`.
 * Write Flow: read-only.
 * Sync Strategy: Pull-based after server write.
 * Conflict Resolution: Server wins.
 *
 * Return value is suitable for use inside `liveQuery(() => getDatasetTemplate(...))`.
 * NEVER write to Dexie inside liveQuery.
 *
 * @errorHandling
 * Strategy:   Returns null when template is not found or is soft-deleted.
 * User Impact: Detail slide-in shows empty/closed state.
 * Logging:     Not required for a pure read.
 */
export async function getDatasetTemplate(id: string): Promise<MigrationDatasetTemplate | null> {
  const db = await getSystemDb();
  const row = await db.legacy_migration_dataset_templates.get(id) as DatasetTemplateDexieDoc | undefined;
  if (!row || row._deleted) return null;
  return row;
}

// ---------------------------------------------------------------------------
// Mutations (POST to server, Dexie updated via sync pull)
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: server Mongo (server-authoritative).
 * Write Flow:
 *   1. POST to /api/system/legacy-migration/dataset-templates.
 *   2. Server validates via templateValidator.ts; returns 422 with issues if guard fails.
 *   3. On success, server saves as draft and returns saved document.
 *   4. Dexie receives it via next system sync pull.
 * Sync Strategy: Pull-based after server write.
 * Conflict Resolution: Server wins; 422 on validation failure.
 *
 * @errorHandling
 * Strategy:   Throws on non-200 responses. 422 means guard failure — caller must surface issues.
 * User Impact: Dataset Builder wizard shows validation errors inline.
 * Logging:     REQUIRED — log template key and creation outcome.
 */
export async function createDatasetTemplate(
  input: CreateDatasetTemplateInput,
): Promise<MigrationDatasetTemplate> {
  const result = await apiFetch(
    '/api/system/legacy-migration/dataset-templates',
    { method: 'POST', body: JSON.stringify(input) },
  );
  return result.template as MigrationDatasetTemplate;
}

/**
 * @localFirst
 * Source of Truth: server Mongo.
 * Write Flow: PUT to /api/system/legacy-migration/dataset-templates/:id; server increments version.
 * Sync Strategy: Pull-based after server write.
 * Conflict Resolution: Server returns 409 if template is active; 422 if validation fails.
 *
 * @errorHandling
 * Strategy:   Throws on non-200 responses; 409 means active template cannot be edited.
 * User Impact: Edit action is disabled in UI when template is active.
 * Logging:     REQUIRED — log template id and update outcome.
 */
export async function updateDatasetTemplate(
  id: string,
  input: UpdateDatasetTemplateInput,
): Promise<MigrationDatasetTemplate> {
  const result = await apiFetch(
    `/api/system/legacy-migration/dataset-templates/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(input) },
  );
  return result.template as MigrationDatasetTemplate;
}

/**
 * @localFirst
 * Source of Truth: server Mongo.
 * Write Flow: DELETE to /api/system/legacy-migration/dataset-templates/:id (soft-delete).
 * Sync Strategy: Pull-based after server write.
 * Conflict Resolution: Server returns 409 if template is active.
 *
 * @errorHandling
 * Strategy:   Throws on non-200 responses; 409 means active template cannot be deleted.
 * User Impact: Delete action is disabled in UI when template is active.
 * Logging:     REQUIRED — log template id and deletion outcome.
 */
export async function softDeleteDatasetTemplate(id: string): Promise<void> {
  await apiFetch(
    `/api/system/legacy-migration/dataset-templates/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

/**
 * @localFirst
 * Source of Truth: server Mongo.
 * Write Flow: POST to /api/system/legacy-migration/dataset-templates/:id/activate.
 *   Server runs templateValidator.ts; returns 400 with issues if guards fail.
 *   On success sets status: 'active'.
 * Sync Strategy: Pull-based after server write.
 * Conflict Resolution: Server wins.
 *
 * @errorHandling
 * Strategy:   Throws on non-200 responses; 400 contains { issues } from templateValidator.ts.
 * User Impact: Activation panel in detail slide-in shows per-guard error list.
 * Logging:     REQUIRED — log template id and activation outcome.
 */
export async function activateDatasetTemplate(id: string): Promise<MigrationDatasetTemplate> {
  const result = await apiFetch(
    `/api/system/legacy-migration/dataset-templates/${encodeURIComponent(id)}/activate`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return result.template as MigrationDatasetTemplate;
}

/**
 * @localFirst
 * Source of Truth: server Mongo.
 * Write Flow: POST to /api/system/legacy-migration/dataset-templates/:id/deactivate.
 *   No guard required. Sets status: 'draft'.
 * Sync Strategy: Pull-based after server write.
 * Conflict Resolution: Server wins.
 *
 * @errorHandling
 * Strategy:   Throws on non-200 responses.
 * User Impact: Deactivation is always available from the detail slide-in.
 * Logging:     REQUIRED — log template id and deactivation outcome.
 */
export async function deactivateDatasetTemplate(id: string): Promise<MigrationDatasetTemplate> {
  const result = await apiFetch(
    `/api/system/legacy-migration/dataset-templates/${encodeURIComponent(id)}/deactivate`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return result.template as MigrationDatasetTemplate;
}

/**
 * @localFirst
 * Source of Truth: server Mongo.
 * Write Flow: POST to /api/system/legacy-migration/dataset-templates/:id/sample.
 *   Server samples SQL data and writes aiMetadata to the template document.
 * Sync Strategy: Pull-based after server write.
 * Conflict Resolution: Server wins.
 *
 * @ai
 * AI Sampling: server-side SQL extraction + AI column profile analysis.
 * Raw rows never stored client-side.
 *
 * @errorHandling
 * Strategy:   Throws on non-200 responses; 501 means not yet implemented.
 * User Impact: AI sampling button in wizard shows error if sampling fails.
 * Logging:     REQUIRED — log template id and sampling outcome.
 */
export async function sampleDatasetTemplate(
  id: string,
  input: SampleDatasetTemplateInput,
): Promise<MigrationDatasetTemplate> {
  const result = await apiFetch(
    `/api/system/legacy-migration/dataset-templates/${encodeURIComponent(id)}/sample`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  return result.template as MigrationDatasetTemplate;
}

/**
 * @contract EnrichDatasetTemplateInput
 *
 * Purpose: Request body for Pass 1 AI column enrichment.
 * Fields: deptId (required legacy department ID), sampleSize (optional, default 50).
 * Rules: deptId must be a positive integer. sampleSize clamped 10–200 server-side.
 * Sync: Server samples SQL + calls GPT-5.4 Pro, writes enrichedAt + columnProfiles to aiMetadata.
 */
export interface EnrichDatasetTemplateInput {
  deptId: number;
  sampleSize?: number;
}

/**
 * @localFirst
 * Source of Truth: server Mongo.
 * Write Flow: POST to /api/system/legacy-migration/dataset-templates/:id/enrich.
 *   Server samples parent + child rows for the given deptId, calls GPT-5.4 Pro to enrich
 *   each column with semanticLabel / dataClassification / distributionSummary, and writes
 *   the enriched columnProfiles back to template.aiMetadata.enrichedAt.
 * Sync Strategy: Pull-based after server write.
 * Conflict Resolution: Server wins; version incremented on each enrich call.
 *
 * @ai
 * Pass 1: server-side SQL sampling + GPT-5.4 Pro column annotation.
 * Raw rows never stored client-side.
 * Can be re-run at any time while status === 'draft'.
 *
 * @errorHandling
 * Strategy:   Throws on non-200 responses; 503 means AI not configured server-side;
 *             409 means template is active and must be deactivated first.
 * User Impact: Wizard AI Sampling step shows enriched column table on success.
 * Logging:     REQUIRED — log template id and enrichment outcome.
 */
export async function enrichDatasetTemplate(
  id: string,
  input: EnrichDatasetTemplateInput,
): Promise<MigrationDatasetTemplate> {
  const result = await apiFetch(
    `/api/system/legacy-migration/dataset-templates/${encodeURIComponent(id)}/enrich`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  return result.template as MigrationDatasetTemplate;
}

/**
 * @localFirst
 * Source of Truth: server Mongo.
 * Write Flow: POST to /api/system/legacy-migration/dataset-templates/:id/match.
 *   Server uses enriched columnProfiles (from Pass 1) + knownCollectionsRegistry to produce
 *   SuggestedMapping[] and (if no target collections selected) SuggestedCollection[].
 *   Writes matchedAt + suggestedMappings + optional suggestedCollections to template.aiMetadata.
 * Sync Strategy: Pull-based after server write.
 * Conflict Resolution: Server wins.
 *
 * @ai
 * Pass 2: GPT-5.4 Pro mapping suggestions (no SQL access; schema metadata only).
 * Requires Pass 1 (enrichDatasetTemplate) to have run first.
 *
 * @errorHandling
 * Strategy:   Throws on non-200 responses; 400 means Pass 1 not yet run;
 *             503 means AI not configured; 409 means template is active.
 * User Impact: Wizard Field Mapping step pre-populates from suggestions on success.
 * Logging:     REQUIRED — log template id and match outcome.
 */
export async function matchDatasetTemplate(
  id: string,
): Promise<MigrationDatasetTemplate> {
  const result = await apiFetch(
    `/api/system/legacy-migration/dataset-templates/${encodeURIComponent(id)}/match`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return result.template as MigrationDatasetTemplate;
}
